import { useEffect, useId, useState } from 'react'
import { Check, Copy, Mail, Shield, X } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { apiErrorDetail } from '@/lib/api/client'
import { invitationApi, type InvitationIssued, type InviteRole } from '@/lib/api/invitations'
import { useAuth } from '@/contexts/AuthContext'
import { UserRole } from '@/types/user'

interface InviteUserModalProps {
  onClose: () => void
  /** Called once an invitation has been created so the parent can refresh its lists. */
  onIssued: () => void
  /** Open directly on the result view (used after "Resend"). */
  initial?: InvitationIssued | null
}

const inputClass = 'w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#5869ff] focus:border-transparent'

/**
 * Modal for inviting a new user by email.
 *
 * Shows an email + role form, then a result view with the single-use invite
 * link, a copy button and a note on whether the invitation email was sent.
 * Admins may only invite viewers; super admins may pick any role (the backend
 * enforces the same rule). Pass `initial` to open straight on the result view,
 * e.g. after resending an existing invitation. Escape closes the dialog.
 */
export function InviteUserModal({ onClose, onIssued, initial = null }: InviteUserModalProps) {
  const { user: currentUser } = useAuth()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<InviteRole>('viewer')
  const [issued, setIssued] = useState<InvitationIssued | null>(initial)
  const [copied, setCopied] = useState(false)
  const headingId = useId()
  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN
  const isResend = initial !== null

  // Close on Escape (same pattern as DetailModal).
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  const mutation = useMutation({
    mutationFn: () => invitationApi.create(email.trim(), role),
    onSuccess: (result) => {
      setIssued(result)
      onIssued()
    },
    onError: (error: unknown) => toast.error(apiErrorDetail(error) || 'Failed to create invitation'),
  })

  const copy = async () => {
    if (!issued) return
    try {
      await navigator.clipboard.writeText(issued.invite_url)
      setCopied(true)
      toast.success('Invite link copied')
    } catch {
      toast.error('Copy failed — select the link and copy it manually')
    }
  }

  const deliveryMessage = issued
    ? issued.email_sent
      ? `Invitation emailed to ${issued.invitation.email}. You can also share the link below.`
      : issued.email_error
        ? `Email failed: ${issued.email_error}. Copy the link and send it yourself.`
        : "Email isn't configured — copy the link and send it yourself."
    : ''

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className="bg-white dark:bg-[#000742] rounded-xl shadow-xl w-full max-w-lg"
      >
        <div className="border-b border-gray-200 dark:border-[#5869ff]/20 px-6 py-4 flex items-center justify-between">
          <h2 id={headingId} className="text-xl font-semibold text-gray-900 dark:text-white">
            {issued ? 'Invitation ready' : 'Invite user'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a1f5c] transition-colors"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {issued ? (
          <div className="p-6 space-y-4">
            <p role="status" className="text-sm text-gray-700 dark:text-gray-300">{deliveryMessage}</p>
            {isResend && (
              <p className="text-sm text-amber-700 dark:text-amber-300">The previous link no longer works.</p>
            )}
            <div className="flex gap-2">
              <input
                aria-label="Invite link"
                readOnly
                value={issued.invite_url}
                onFocus={event => event.currentTarget.select()}
                className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-[#5869ff]/30 bg-gray-50 dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white text-sm"
              />
              <button
                type="button"
                onClick={() => void copy()}
                className="inline-flex items-center gap-2 px-3 py-2 bg-[#5869ff] text-white rounded-lg hover:bg-[#4757e6] transition-colors"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied' : 'Copy link'}
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Role: {issued.invitation.role.replace('_', ' ')} · Expires{' '}
              {new Date(issued.invitation.expires_at).toLocaleDateString()} · Single use.
            </p>
            <div className="flex justify-end pt-2">
              <button
                type="button"
                autoFocus
                onClick={onClose}
                className="px-4 py-2 bg-[#5869ff] text-white rounded-lg hover:bg-[#4757e6] transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={event => {
              event.preventDefault()
              mutation.mutate()
            }}
            className="p-6 space-y-5"
          >
            <div>
              <label htmlFor="invite-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="invite-email"
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={event => setEmail(event.target.value)}
                  placeholder="person@example.com"
                  className={inputClass}
                />
              </div>
            </div>
            <div>
              <label htmlFor="invite-role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Role
              </label>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  id="invite-role"
                  value={role}
                  onChange={event => setRole(event.target.value as InviteRole)}
                  className={`${inputClass} appearance-none`}
                >
                  <option value="viewer">Viewer</option>
                  {isSuperAdmin && <option value="admin">Admin</option>}
                  {isSuperAdmin && <option value="super_admin">Super Admin</option>}
                </select>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Viewers can see every dashboard and download the IB Review, but cannot change configuration.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-200 dark:border-[#5869ff]/20">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-[#5869ff]/30 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a1f5c] transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={mutation.isPending}
                className="px-4 py-2 bg-[#5869ff] text-white rounded-lg hover:bg-[#4757e6] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {mutation.isPending ? 'Sending…' : 'Send invitation'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
